"use client";

import { ReactNode } from "react";
import { cn } from "@drpcrd/utils/shared";
import { useSidebarContext } from "./sidebar-context";

type Props = { children: ReactNode };

/**
 * Scrollable navigation slot for a `Sidebar` — compose it from
 * `SidebarNavItem`s (or anything else). On the desktop collapsed rail, the
 * same children render again in a centered icon-only column; each
 * `SidebarNavItem` adapts its own compact rendering via
 * `useSidebarContext()` rather than this component needing a separate
 * `collapsedIcons` list.
 */
export const SidebarBody = ({ children }: Props) => {
  const { slot, isExpanded, showContent } = useSidebarContext();

  if (slot === "mobile-bar") return null;

  if (slot === "mobile-menu") {
    return <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</div>;
  }

  // Desktop
  if (!isExpanded) {
    return <div className="mt-2 flex flex-1 flex-col items-center gap-0.5 px-0.5">{children}</div>;
  }

  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-in-out",
        showContent ? "opacity-100" : "opacity-0",
      )}
    >
      {children}
    </div>
  );
};
